import { useCallback, useEffect, useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Autocomplete,
  TextField,
  Stack,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import apiService from '../../services/api.service';
import type { ScanEntry } from '../../types/warehouse';

interface ResolveQueueDialogProps {
  open: boolean;
  companyId: number;
  entry: ScanEntry | null;
  onClose: () => void;
  /** Called after a successful map/create with the resolved patch for the entry. */
  onResolved: (entryId: string, patch: Partial<ScanEntry>) => void;
}

interface ItemOption {
  itemCode: string;
  itemName: string;
  itemGroupName?: string;
  itemGroupCode?: string;
}

/**
 * On-device map-or-create review queue for an unresolved scan.
 *  - Map: link the scanned barcode/GTIN to an EXISTING item (common case — the
 *    item exists but the GTIN isn't in OBCD yet). Resolves immediately + queues
 *    an OBCD sync-back.
 *  - Create: capture minimum new-item fields as a GOVERNED, quarantined request.
 */
export default function ResolveQueueDialog({ open, companyId, entry, onClose, onResolved }: ResolveQueueDialogProps): JSX.Element {
  const [tab, setTab] = useState(0);
  const [options, setOptions] = useState<ItemOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ItemOption | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Create form
  const [desc, setDesc] = useState('');
  const [group, setGroup] = useState('');
  const [supplier, setSupplier] = useState('');
  const [uom, setUom] = useState('');
  const [busy, setBusy] = useState(false);

  const code = entry?.barcode || entry?.gtin || '';
  const codeType: 'barcode' | 'gtin' = entry?.barcode ? 'barcode' : 'gtin';

  // Reset the form whenever the dialog opens for a different scan so a previous
  // entry's typed values don't bleed across.
  useEffect(() => {
    if (open) {
      setTab(0);
      setSelected(null);
      setError(null);
      setOptions([]);
      setDesc('');
      setGroup('');
      setSupplier('');
      setUom('');
    }
  }, [open, entry?.id]);

  const search = useCallback(
    async (q: string) => {
      if (!companyId || q.length < 2) return;
      setLoading(true);
      try {
        const res: any = await apiService.searchWarehouseItems(companyId, q);
        setOptions(Array.isArray(res?.data) ? res.data : []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [companyId]
  );

  const doMap = useCallback(async () => {
    if (!entry || !selected) return;
    setBusy(true);
    setError(null);
    try {
      await apiService.warehouseMapOrCreate({
        companyId,
        code,
        codeType,
        source: 'map',
        itemCode: selected.itemCode,
        itemDescription: selected.itemName,
      });
      onResolved(entry.id, {
        itemNo: selected.itemCode,
        itemDescription: selected.itemName,
        itemGroup: selected.itemGroupName || selected.itemGroupCode || '',
        resolution: 'mapped',
      });
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Map failed');
    } finally {
      setBusy(false);
    }
  }, [entry, selected, companyId, code, codeType, onResolved, onClose]);

  const doCreate = useCallback(async () => {
    if (!entry || !desc.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await apiService.warehouseMapOrCreate({
        companyId,
        code,
        codeType,
        source: 'create',
        newItemFields: { itemDescription: desc.trim(), itemGroup: group.trim(), supplier: supplier.trim(), uom: uom.trim() },
      });
      onResolved(entry.id, {
        itemDescription: desc.trim(),
        itemGroup: group.trim(),
        resolution: 'created_pending',
      });
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Create request failed');
    } finally {
      setBusy(false);
    }
  }, [entry, desc, group, supplier, uom, companyId, code, codeType, onResolved, onClose]);

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resolve unmatched scan</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Scanned {codeType === 'barcode' ? '1D barcode' : 'GTIN'}:
          </Typography>
          <Typography sx={{ fontFamily: 'monospace' }}>{code || '—'}</Typography>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Map to existing item" />
          <Tab label="Create new item (request)" />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Autocomplete
              options={options}
              loading={loading}
              value={selected}
              getOptionLabel={(o) => `${o.itemCode} — ${o.itemName}`}
              isOptionEqualToValue={(a, b) => a.itemCode === b.itemCode}
              onInputChange={(_e, v, reason) => {
                if (reason === 'input') void search(v);
              }}
              onChange={(_e, v) => setSelected(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search item by code or description"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              The barcode↔item link is saved locally and queued for write-back to SAP Bar Codes (OBCD).
            </Typography>
          </Box>
        )}

        {tab === 1 && (
          <Stack spacing={1.5}>
            <Alert severity="info">
              A new-item request is <strong>quarantined</strong> — it exports to the Exceptions sheet and is not posted as
              a confirmed lot entry until the item exists in SAP B1.
            </Alert>
            <TextField label="Item description" size="small" value={desc} onChange={(e) => setDesc(e.target.value)} required />
            <TextField label="Item group" size="small" value={group} onChange={(e) => setGroup(e.target.value)} />
            <TextField label="Supplier" size="small" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
            <TextField label="UoM" size="small" value={uom} onChange={(e) => setUom(e.target.value)} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {tab === 0 ? (
          <Button variant="contained" onClick={doMap} disabled={!selected || busy}>
            Map
          </Button>
        ) : (
          <Button variant="contained" onClick={doCreate} disabled={!desc.trim() || busy}>
            Save request
          </Button>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}
